import {
  mergeAnnotationsLatest,
  type AnnotationsWithMetricOutputs,
} from "@/human-feedback/annotations"

import { annotationHasValue, type Annotation } from "@/types/api/Annotation"

import type {
  GroupedSimulationData,
  HumanReviewData,
  MetricOutputData,
} from "../types/simulationAnnotations"
import { formatDate } from "./simulationPresentation"

export type {
  MetricOutputData,
  HumanReviewData,
  GroupedSimulationData,
} from "../types/simulationAnnotations"

export { formatDate, getHumanReviewValue } from "./simulationPresentation"

/**
 * Collapses multiple non-archived annotations from the same reviewer for a
 * single (sim_output, metric) into a single merged annotation. The latest row
 * wins for identity; older same-reviewer rows gap-fill missing value/note/json
 * fields. Anonymous (empty-assignee) rows are passed through untouched. If a
 * group includes any PRIMARY-priority row, the merged result inherits that
 * priority so primary-counting logic downstream still picks the right entry.
 */
const mergePerReviewer = (rows: Annotation[]): Annotation[] => {
  const byReviewer = new Map<string, Annotation[]>()
  const anonymous: Annotation[] = []
  for (const row of rows) {
    if (!row.assignee) {
      anonymous.push(row)
      continue
    }
    const existing = byReviewer.get(row.assignee)
    if (existing) {
      existing.push(row)
    } else {
      byReviewer.set(row.assignee, [row])
    }
  }

  const merged: Annotation[] = []
  for (const group of byReviewer.values()) {
    if (group.length === 1) {
      merged.push(group[0])
      continue
    }
    const mergedRow = mergeAnnotationsLatest(group)
    if (!mergedRow) {
      continue
    }
    // mergeAnnotationsLatest inherits the newest row's created_at, but the
    // downstream "first by created_at wins" fallback for primary selection
    // would then flip a reviewer who started earliest but added a follow-up
    // note. Pin the merged row's created_at to the group min.
    const earliestCreatedAt = group.reduce(
      (earliest, row) =>
        new Date(row.created_at).getTime() < new Date(earliest).getTime()
          ? row.created_at
          : earliest,
      group[0].created_at,
    )
    const hasPrimary = group.some((r) => r.priority === "PRIMARY")
    merged.push({
      ...mergedRow,
      created_at: earliestCreatedAt,
      ...(hasPrimary ? { priority: "PRIMARY" as const } : {}),
    })
  }
  return [...merged, ...anonymous]
}

/**
 * Groups annotations by simulation output ID and builds the data structure.
 * When `includeUnreviewed` is true, also includes sims that have a metric
 * output but no human annotations.
 */
export const groupAnnotationsBySimulation = (
  annotationsWithMetricOutputs: AnnotationsWithMetricOutputs | undefined,
  metricId: string | undefined,
  outputType: string,
  includeUnreviewed = false,
): GroupedSimulationData[] => {
  if (!annotationsWithMetricOutputs || !metricId) {
    return []
  }

  const metricAnnotations =
    annotationsWithMetricOutputs.annotations[metricId] || []
  const metricOutputs =
    annotationsWithMetricOutputs.metricOutputs[metricId] || {}

  // Filter to only non-archived annotations that have a value or a comment
  const activeAnnotations = metricAnnotations.filter(
    (annotation) =>
      !annotation.archived &&
      (annotationHasValue(annotation) ||
        !!annotation.annotations?.custom_explanation),
  )

  // Group annotations by simulation_output_id
  const groupedBySimOutput = new Map<
    string,
    {
      annotations: typeof activeAnnotations
      metricOutput: (typeof metricOutputs)[string] | undefined
    }
  >()

  for (const annotation of activeAnnotations) {
    const simId = annotation.simulation_output_id
    if (!groupedBySimOutput.has(simId)) {
      groupedBySimOutput.set(simId, {
        annotations: [],
        metricOutput: metricOutputs[simId],
      })
    }
    groupedBySimOutput.get(simId)!.annotations.push(annotation)
  }

  // Include sims with metric output but no annotations
  if (includeUnreviewed) {
    for (const simId of Object.keys(metricOutputs)) {
      if (!groupedBySimOutput.has(simId)) {
        groupedBySimOutput.set(simId, {
          annotations: [],
          metricOutput: metricOutputs[simId],
        })
      }
    }
  }

  // Convert to grouped data format
  return Array.from(groupedBySimOutput.entries()).map(
    ([
      simulationOutputId,
      { annotations: unsortedAnnotations, metricOutput },
    ]) => {
      const annotations = mergePerReviewer(unsortedAnnotations).sort((a, b) => {
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )
      })

      // Determine which annotation is counted as primary for stats
      // Priority: PRIMARY annotation first, then first by creation order
      const primaryAnnotationIndex = annotations.findIndex(
        (a) => a.priority === "PRIMARY",
      )
      const countedIndex =
        primaryAnnotationIndex !== -1 ? primaryAnnotationIndex : 0

      // Build human reviews array
      const humanReviews: HumanReviewData[] = annotations.map(
        (annotation, index) => ({
          id: annotation.id,
          output_type: outputType,
          float_value: annotation.ground_truth_float_value,
          string_value: annotation.ground_truth_string_value,
          set_value: annotation.ground_truth_set_value,
          json_value:
            (annotation.ground_truth_json as Record<string, unknown>) ?? null,
          explanation: annotation.annotations?.custom_explanation || null,
          reviewer: annotation.assignee || null,
          date: formatDate(annotation.last_updated_at),
          dateRaw: annotation.last_updated_at
            ? String(annotation.last_updated_at)
            : null,
          isCountedAsPrimary: index === countedIndex,
          hasValue: annotationHasValue(annotation),
        }),
      )

      // Build raw metric output
      const rawMetricOutput: MetricOutputData | null = metricOutput
        ? {
            output_type: metricOutput.output_type,
            float_value: metricOutput.float_value,
            string_value: metricOutput.string_value,
            set_value: metricOutput.set_value ?? null,
            result: metricOutput.result ?? null,
            result_json: metricOutput.result_json ?? null,
            metric_version_number: metricOutput.metric_version_number ?? null,
            metric_version_ulid: metricOutput.metric_version_ulid ?? null,
          }
        : null

      return {
        simulationOutputId,
        humanReviews,
        rawAnnotations: annotations,
        rawPairwiseAnnotations: unsortedAnnotations,
        rawMetricOutput,
      }
    },
  )
}
